import { useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";

interface LightboxProps {
  images: string[];
  name: string;
  initialIndex: number;
  onClose: () => void;
}

export default function Lightbox({ images, name, initialIndex, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex);
  const [direction, setDirection] = useState<"left" | "right" | null>(null);
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade-in on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 280);
  }, [onClose]);

  const navigate = useCallback(
    (dir: "left" | "right") => {
      if (animating) return;
      setDirection(dir);
      setAnimating(true);
      setTimeout(() => {
        setCurrent((prev) =>
          dir === "right"
            ? (prev + 1) % images.length
            : (prev - 1 + images.length) % images.length
        );
        setDirection(null);
        setAnimating(false);
      }, 220);
    },
    [animating, images.length]
  );

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowRight") navigate("right");
      if (e.key === "ArrowLeft") navigate("left");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose, navigate]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const slideClass = animating && direction
    ? direction === "right"
      ? "-translate-x-6 opacity-0"
      : "translate-x-6 opacity-0"
    : "translate-x-0 opacity-100";

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex flex-col transition-opacity duration-280 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      style={{ backgroundColor: "rgba(15, 13, 11, 0.96)" }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <p
          className="text-sm font-light text-white/60 tracking-widest truncate max-w-xs"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {name}
        </p>
        <div className="flex items-center gap-4">
          <span className="text-[11px] tracking-widest text-white/40">
            {current + 1} <span className="opacity-40">/</span> {images.length}
          </span>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 flex items-center justify-center px-14 min-h-0 relative"
        onClick={handleClose}
      >
        {/* Image */}
        <div
          className={`relative h-full max-h-full transition-all duration-220 ease-out ${slideClass}`}
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "min(700px, 90vw)" }}
        >
          <img
            key={current}
            src={images[current]}
            alt={`${name} — view ${current + 1}`}
            className="h-full w-auto max-w-full object-contain object-center select-none"
            style={{ maxHeight: "calc(100vh - 200px)" }}
            draggable={false}
          />
        </div>

        {/* Left arrow */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate("left"); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center
              border border-white/10 text-white/50 hover:text-white hover:border-white/30
              transition-all duration-200 cursor-pointer backdrop-blur-sm"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
        )}

        {/* Right arrow */}
        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate("right"); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center
              border border-white/10 text-white/50 hover:text-white hover:border-white/30
              transition-all duration-200 cursor-pointer backdrop-blur-sm"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="shrink-0 py-4 px-6">
          <div className="flex items-center justify-center gap-2.5">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => {
                  if (i === current || animating) return;
                  setDirection(i > current ? "right" : "left");
                  setAnimating(true);
                  setTimeout(() => {
                    setCurrent(i);
                    setDirection(null);
                    setAnimating(false);
                  }, 220);
                }}
                className={`w-12 h-14 overflow-hidden shrink-0 transition-all duration-200 cursor-pointer ${
                  i === current
                    ? "ring-1 ring-white/80 opacity-100"
                    : "opacity-35 hover:opacity-70"
                }`}
                aria-label={`Go to image ${i + 1}`}
              >
                <img
                  src={img}
                  alt={`${name} thumbnail ${i + 1}`}
                  className="w-full h-full object-cover object-top"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ESC hint */}
      <p className="text-center text-[10px] tracking-widest text-white/20 pb-3 shrink-0">
        Press ESC or click outside to close
      </p>
    </div>,
    document.body
  );
}
